/**
 * Outreach v2 attachment drawer (Task 27) — GET /roster/{entry_id}/outreach/
 * attachments (Task 23's list route) and the download URL for the stream
 * route. Wire JSON is snake_case; the FE type is camelCase, same convention
 * as outreachApi.ts's own wire->FE mapping.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';
import { rosterOutreachPath } from './outreachApi';

export interface OutreachAttachment {
  id: number;
  filename: string;
  contentType: string;
  sizeBytes: number;
  receivedAt: string;
  isInline?: boolean;
}

interface WireAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  received_at: string;
  is_inline?: boolean;
}

export function fetchOutreachAttachments(entryId: string): Promise<OutreachAttachment[]> {
  return apiClient
    .get<DataResponse<WireAttachment[]>>(rosterOutreachPath(entryId, '/attachments'))
    .then((res) => res.data.data.map((a) => ({
      id: a.id, filename: a.filename, contentType: a.content_type, sizeBytes: a.size_bytes, receivedAt: a.received_at, isInline: a.is_inline === true,
    })));
}

/** The download route is outreach:read-scoped like every other outreach
 *  endpoint — apiClient's Bearer-token interceptor only fires on requests it
 *  makes, so a plain <a href> (no custom headers) would 401. Fetch the bytes
 *  through apiClient instead and trigger the save the same way
 *  ImportUploadStep.tsx's template download already does: createObjectURL +
 *  a programmatic <a download> click. */
export async function downloadOutreachAttachment(entryId: string, attachmentId: number, filename: string): Promise<void> {
  const res = await apiClient.get<Blob>(rosterOutreachPath(entryId, `/attachments/${attachmentId}`), { responseType: 'blob' });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
